import { create, type StateCreator } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { AsyncStoreActions, BaseStore, StoreConfig } from "../types";

/**
 * Create a store with common middleware and configuration
 *
 * @example
 * ```typescript
 * const useMyStore = createStore(
 *   (set) => ({
 *     count: 0,
 *     increment: () => set((state) => { state.count += 1; }),
 *   }),
 *   {
 *     name: 'my-store',
 *     persist: true,
 *     version: 2,
 *     migrate: (persistedState: unknown, version: number) => {
 *       if (version < 2) {
 *         // Handle migration from version 1 to 2
 *         const oldState = persistedState as { count: number };
 *         return { ...oldState, newField: 'default' };
 *       }
 *       return persistedState as MyStoreState;
 *     }
 *   }
 * );
 * ```
 */
export function createStore<T extends BaseStore>(
  storeFunction: StateCreator<T, [["zustand/immer", never]], [], T>,
  config: StoreConfig<T>,
) {
  const {
    name,
    persist: shouldPersist = false,
    version = 1,
    partialize,
    onRehydrateStorage,
    migrate,
  } = config;

  if (shouldPersist) {
    // Add environment prefix for environment isolation
    const currentEnv = import.meta.env.VITE_ENV || "development";
    const environmentalName = `${currentEnv}-${name}`;

    const persistConfig = {
      name: environmentalName,
      version,
      storage: createJSONStorage(() => localStorage),
      migrate:
        migrate ||
        ((persistedState: unknown, _version: number) => {
          // Default migration: return the persisted state as-is
          // This prevents the "no migrate function" warning
          // For custom migration logic, provide a migrate function in the config
          return persistedState as T;
        }),
      ...(partialize && { partialize }),
      ...(onRehydrateStorage && { onRehydrateStorage }),
    };

    return create<T>()(persist(immer(storeFunction), persistConfig));
  }

  return create<T>()(immer(storeFunction));
}

/**
 * Create async actions for stores that handle loading states
 */
export function createAsyncActions<T extends AsyncStoreActions>(): Pick<
  T,
  keyof AsyncStoreActions
> {
  return {
    loading: false,
    error: null,
    setLoading: () => {}, // This should be overridden by the store
    setError: () => {}, // This should be overridden by the store
    clearError: () => {}, // This should be overridden by the store
  } as Pick<T, keyof AsyncStoreActions>;
}

/**
 * Higher-order function to add logging to store actions
 */
export function withLogging<T extends BaseStore>(
  _storeName: string,
  storeFunction: StateCreator<T, [["zustand/immer", never]], [], T>,
): StateCreator<T, [["zustand/immer", never]], [], T> {
  return (set, get, api) => {
    const loggedSet: typeof set = (args) => {
      if (process.env.NODE_ENV === "development") {
        // console.group(`🏪 ${storeName} State Update`);
        set(args);
        console.groupEnd();
      } else {
        set(args);
      }
    };

    return storeFunction(loggedSet, get, api);
  };
}
